import { Button } from '@bennows/boows-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api, call } from '../../lib/api';
import { useToast } from '../../lib/toasts';
import { ME_QUERY_KEY, type OperatorMe } from '../auth/useOperatorMe';
import { BrandMark } from './BrandMark';
import { CONSOLE_NAV } from './navigation';

/**
 * The console frame (spec 140 §4.5): skip link, from 1024 px the sidebar with
 * the navigation and the signed-in operator; below it a top bar with brand and
 * sign-out and the same entries as a fixed bottom tab bar. `main#main` takes
 * focus after a navigation.
 */
export function ConsoleShell({ me }: { readonly me: OperatorMe }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const mainRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);
  const entries = CONSOLE_NAV.filter((entry) => !entry.permission || me.permissions.includes(entry.permission));

  const signOut = useMutation({
    mutationFn: () => call(() => api.DELETE('/v1/operator/sessions/current')),
    onSuccess: () => {
      queryClient.clear();
      queryClient.setQueryData(ME_QUERY_KEY, null);
      navigate('/sign-in', { replace: true });
    },
    onError: () => toast(t('shell.signOutFailed'), 'error'),
  });

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo(0, 0);
    (mainRef.current?.querySelector<HTMLElement>('h1') ?? mainRef.current)?.focus({ preventScroll: true });
  }, [location.pathname]);

  const sidebarNav = (
    <nav aria-label={t('shell.navLabel')} className="ui-shell__nav">
      <div className="ui-nav-group__list">
        {entries.map(({ key, path, icon: Icon, end }) => (
          <NavLink key={key} to={path} end={end} className="ui-nav-item">
            <Icon aria-hidden="true" />
            {t(`nav.${key}`)}
          </NavLink>
        ))}
      </div>
    </nav>
  );

  return (
    <div className="ui-shell">
      <a className="ui-skip-link" href="#main">
        {t('shell.skipLink')}
      </a>
      <aside className="ui-sidebar">
        <NavLink to="/" className="ui-brand">
          <BrandMark />
        </NavLink>
        {sidebarNav}
        <div className="ui-console-who">
          <p className="ui-console-who__name">{me.display_name}</p>
          <p className="ui-console-who__role">{t(`roles.${me.role}`)}</p>
          <Button variant="ghost" size="sm" iconPrefix={<LogOut aria-hidden="true" />} loading={signOut.isPending} onClick={() => signOut.mutate()}>
            {t('shell.signOut')}
          </Button>
        </div>
      </aside>
      <div className="ui-console-topbar">
        <NavLink to="/" className="ui-brand">
          <BrandMark />
        </NavLink>
        <Button variant="ghost" size="sm" iconPrefix={<LogOut aria-hidden="true" />} loading={signOut.isPending} onClick={() => signOut.mutate()}>
          {t('shell.signOut')}
        </Button>
      </div>
      <main id="main" ref={mainRef} className="ui-shell__main" tabIndex={-1}>
        <Outlet context={me} />
      </main>
      <nav aria-label={t('shell.navLabel')} className="ui-bottomnav ui-console-tabs">
        {entries.map(({ key, path, icon: Icon, end }) => (
          <NavLink key={key} to={path} end={end} className="ui-bottomnav__item">
            <span className="ui-bottomnav__icon">
              <Icon aria-hidden="true" size={20} />
            </span>
            {t(`navTab.${key}`)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
